import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';

// Assuming API is served from the same origin or configured proxy
// For local development, if your Hono app runs on a different port (e.g., 8787)
// you might need to adjust this or use a proxy.
// const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:8787' : '';
const API_BASE_URL = ''; // Assuming same origin or proxy handles it.

const RealTimeConversation = ({ setCurrentPage }) => {
  const [currentClientId, setCurrentClientId] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [statusMessage, setStatusMessage] = useState('Listo');
  const [isStatusError, setIsStatusError] = useState(false);
  const [jsonOutputData, setJsonOutputData] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [remoteAudioElement, setRemoteAudioElement] = useState(null);

  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const localStreamRef = useRef(null);

  // Define functions callable by the AI via data channel
  const callableFunctions = {
    getClientInfo: async ({ clientId: requestedClientId }) => {
      // If AI calls getClientInfo without specifying clientId, use currentClientId
      const idToFetch = requestedClientId || currentClientId;
      if (!idToFetch) {
        return { success: false, error: 'No client ID available to fetch.' };
      }
      try {
        const response = await fetch(`${API_BASE_URL}/api/client/${idToFetch}`);
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        const data = await response.json();
        // Update clientInfo state if it's the currently loaded client
        if (idToFetch === currentClientId) {
          setClientInfo(data);
        }
        return { success: true, data };
      } catch (error) {
        console.error('Error in getClientInfo function:', error);
        return { success: false, error: error.message };
      }
    },
    updateClientPreferences: async ({ preferences }) => {
      if (!currentClientId) {
        return { success: false, error: 'No client loaded' };
      }
      try {
        const response = await fetch(`${API_BASE_URL}/api/client/${currentClientId}/preferences`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(preferences),
        });
        const data = await response.json();
        if (data.success) {
          // Update the display with new preferences
          setClientInfo(prevInfo => ({
            ...prevInfo,
            preferences: data.preferences,
          }));
        }
        return { success: true, data };
      } catch (error) {
        console.error('Error updating client preferences:', error);
        return { success: false, error: error.message };
      }
    },
  };

  const updateStatus = (message, isError = false) => {
    setStatusMessage(message);
    setIsStatusError(isError);
  };

  const loadClient = async (clientId) => {
    updateStatus(`Cargando cliente ${clientId}...`);
    try {
      // Use the callable function structure for consistency, though it's initiated by user here
      const result = await callableFunctions.getClientInfo({ clientId });
      if (result.success) {
        setCurrentClientId(clientId);
        setClientInfo(result.data);
        updateStatus('Cliente cargado exitosamente');
      } else {
        throw new Error(result.error || 'Failed to load client data');
      }
    } catch (error) {
      console.error('Error al cargar el cliente:', error);
      updateStatus(`Error al cargar la información del cliente: ${error.message}`, true);
    }
  };

  const configureDataChannel = () => {
    if (!dataChannelRef.current) return;
    console.log('Configurando canal de datos');
    const event = {
      type: 'session.update',
      session: {
        modalities: ['text', 'audio'],
        tools: [
          {
            type: 'function',
            name: 'getClientInfo',
            description: 'Obtiene la información actual del cliente. Si no se especifica clientId, usa el cliente actualmente cargado.',
            parameters: {
              type: 'object',
              properties: {
                 clientId: { type: 'string', description: 'El ID del cliente (opcional)' }
              },
              required: []
            }
          },
          {
            type: 'function',
            name: 'updateClientPreferences',
            description: 'Actualiza las preferencias del cliente actualmente cargado.',
            parameters: {
              type: 'object',
              properties: {
                preferences: {
                  type: 'object',
                  properties: {
                    priceRange: { type: 'string', description: 'Rango de precio de la propiedad' },
                    city: { type: 'string', description: 'Ciudad de interés' },
                    bedrooms: { type: 'string', description: 'Número de habitaciones' },
                    bathrooms: { type: 'string', description: 'Número de baños' },
                    size: { type: 'string', description: 'Tamaño en metros cuadrados' }
                  },
                  required: [] // Make individual preferences optional
                }
              },
              required: ['preferences']
            }
          }
        ],
      },
    };
    dataChannelRef.current.send(JSON.stringify(event));
  };
  
  useEffect(() => {
    // Cleanup function for when the component unmounts
    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      if (remoteAudioElement && remoteAudioElement.parentNode) {
        remoteAudioElement.parentNode.removeChild(remoteAudioElement);
      }
    };
  }, [remoteAudioElement]);


  const startVoiceAssistant = async () => {
    if (!currentClientId) {
      alert('Por favor, cargue un cliente primero');
      updateStatus('Error: Cliente no cargado', true);
      return;
    }

    updateStatus('Iniciando asistente de voz...');
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;

      peerConnectionRef.current = new RTCPeerConnection(); // Add STUN servers if needed: { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] }

      // Setup data channel
      dataChannelRef.current = peerConnectionRef.current.createDataChannel('response');
      dataChannelRef.current.onopen = (event) => {
        console.log('Canal de datos abierto', event);
        configureDataChannel();
        updateStatus('Conectado al asistente (canal de datos abierto)');
      };
      dataChannelRef.current.onmessage = async (event) => {
        console.log('Mensaje del canal de datos:', event.data);
        const msg = JSON.parse(event.data);
        if (msg.type === 'response.function_call_arguments.done') {
          const fn = callableFunctions[msg.name];
          if (fn) {
            console.log(`Llamando función local ${msg.name} con ${msg.arguments}`);
            try {
              const args = JSON.parse(msg.arguments || '{}'); // Ensure args is an object
              const result = await fn(args);
              console.log('Resultado de la función local:', result);
              const responseEvent = {
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: msg.call_id,
                  output: JSON.stringify(result),
                },
              };
              dataChannelRef.current.send(JSON.stringify(responseEvent));
            } catch (e) {
              console.error("Error processing function call or arguments:", e, msg.arguments);
              const errorResult = { success: false, error: `Error processing function call: ${e.message}` };
              const responseEvent = {
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: msg.call_id,
                  output: JSON.stringify(errorResult),
                },
              };
              dataChannelRef.current.send(JSON.stringify(responseEvent));
            }
          } else {
            console.warn(`Función desconocida llamada: ${msg.name}`);
          }
        }
      };
       dataChannelRef.current.onerror = (error) => {
        console.error("Error en el canal de datos:", error);
        updateStatus("Error en el canal de datos.", true);
      };
      dataChannelRef.current.onclose = () => {
        console.log("Canal de datos cerrado.");
        updateStatus("Canal de datos cerrado.");
      };


      stream.getTracks().forEach((track) =>
        peerConnectionRef.current.addTransceiver(track, { direction: 'sendrecv' })
      );

      peerConnectionRef.current.ontrack = (event) => {
        console.log('Recibida pista remota:', event.streams);
        if (event.streams && event.streams[0]) {
          const newAudio = document.createElement('audio');
          newAudio.srcObject = event.streams[0];
          newAudio.autoplay = true;
          newAudio.controls = true; // Make visible for debugging
          document.body.appendChild(newAudio); // Append somewhere visible or manage via React state
          setRemoteAudioElement(newAudio);
          updateStatus('Audio remoto conectado.');
        }
      };
      
      peerConnectionRef.current.onicecandidate = (event) => {
        // Typically, ICE candidates are sent to the other peer.
        // In this setup, the offer/answer handles SDP exchange directly.
        // If trickle ICE is needed, this is where candidates would be sent.
        if (event.candidate) {
          console.log("Generated ICE candidate:", event.candidate);
        } else {
          console.log("All ICE candidates have been gathered.");
        }
      };

      const offer = await peerConnectionRef.current.createOffer();
      await peerConnectionRef.current.setLocalDescription(offer);

      const response = await fetch(`${API_BASE_URL}/api/rtc-connect`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          'Content-Type': 'application/sdp',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error de conexión RTC: ${response.status} - ${errorText}`);
      }

      const answerSdp = await response.text();
      await peerConnectionRef.current.setRemoteDescription({
        sdp: answerSdp,
        type: 'answer',
      });
      updateStatus('Asistente de voz conectado y listo');

    } catch (error) {
      console.error('Error al iniciar el asistente de voz:', error);
      updateStatus(`Error al iniciar el asistente: ${error.message}`, true);
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
    }
  };

  const handleAudioFileChange = (event) => {
    setAudioFile(event.target.files[0]);
    setJsonOutputData(null); // Clear previous results
  };

  const processAudioFile = async () => {
    if (!audioFile) {
      alert('Por favor, seleccione un archivo de audio');
      return;
    }

    updateStatus('Procesando archivo de audio...');
    const formData = new FormData();
    formData.append('audio', audioFile);
    // The 'model' and other params are set on the backend as per your index.ts

    try {
      const response = await fetch(`${API_BASE_URL}/api/process-audio`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorJson = {};
        try {
            errorJson = JSON.parse(errorText);
        } catch (e) { /* ignore parsing error if not json */ }
        throw new Error(`Error en el procesamiento: ${response.status} - ${errorJson.error || errorText}`);
      }

      const data = await response.json();
      setJsonOutputData(data);
      updateStatus('Procesamiento completado');
    } catch (error) {
      console.error('Error:', error);
      updateStatus(`Error al procesar el archivo: ${error.message}`, true);
      setJsonOutputData({ error: `Error al procesar el archivo: ${error.message}` });
    }
  };
  
  const styles = {
    clientInfo: {
      border: '1px solid #ccc',
      padding: '20px',
      margin: '20px 0',
      borderRadius: '5px',
    },
    controls: {
      margin: '20px 0',
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap',
    },
    button: {
      padding: '10px 20px',
      // marginRight: '10px', // Replaced by gap in controls
      cursor: 'pointer',
      border: '1px solid #ddd',
      borderRadius: '4px',
      backgroundColor: '#f0f0f0',
    },
    status: {
      marginTop: '20px',
      padding: '10px',
      borderRadius: '5px',
      backgroundColor: '#f0f0f0',
    },
    audioUpload: {
      margin: '20px 0',
      padding: '20px',
      border: '2px dashed #ccc',
      borderRadius: '5px',
      textAlign: 'center',
    },
    jsonOutput: {
      whiteSpace: 'pre-wrap',
      background: '#f5f5f5',
      padding: '15px',
      borderRadius: '5px',
      marginTop: '20px',
      textAlign: 'left',
    },
    transcriptContainer: {
      marginTop: '20px',
      marginBottom: '20px',
    },
    transcript: {
      whiteSpace: 'pre-wrap',
      background: '#f8f8f8',
      padding: '15px',
      borderRadius: '5px',
      borderLeft: '4px solid #4CAF50', // Green from example
      marginBottom: '20px',
      textAlign: 'left',
    },
    container: {
      fontFamily: 'Arial, sans-serif',
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
    },
     backButton: {
      marginRight: '1rem',
      padding: '0.5rem',
      borderRadius: '9999px', // full
      transition: 'background-color 0.2s',
    },
    backButtonHover: { // Define hover style if needed or manage with CSS classes
      backgroundColor: '#e2e8f0', // gray-200
    },
    header: {
      marginBottom: '1.5rem', // mb-6
      display: 'flex',
      alignItems: 'center',
    },
    title: {
      fontSize: '1.5rem', // text-2xl
      fontWeight: '600', // font-semibold
    }
  };


  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button 
          onClick={() => setCurrentPage('home')} 
          style={styles.backButton}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={styles.title}>Asistente de Voz Inmobiliario</h1>
      </div>
      
      <div style={styles.clientInfo}>
        <h2>Información del Cliente</h2>
        {clientInfo ? (
          <div>
            <h3>{clientInfo.name}</h3>
            {clientInfo.id && <p><strong>ID:</strong> {clientInfo.id}</p>}
            <p><strong>Rango de Precio:</strong> {clientInfo.preferences?.priceRange || 'No establecido'}</p>
            <p><strong>Ciudad:</strong> {clientInfo.preferences?.city || 'No establecida'}</p>
            <p><strong>Habitaciones:</strong> {clientInfo.preferences?.bedrooms || 'No establecido'}</p>
            <p><strong>Baños:</strong> {clientInfo.preferences?.bathrooms || 'No establecido'}</p>
            <p><strong>Tamaño:</strong> {clientInfo.preferences?.size || 'No establecido'}</p>
          </div>
        ) : (
          <p>Seleccione un cliente para cargar su información.</p>
        )}
      </div>

      <div style={styles.controls}>
        <button style={styles.button} onClick={() => loadClient('123')}>Cargar Cliente de Prueba (ID: 123)</button>
        <button style={styles.button} onClick={startVoiceAssistant}>Iniciar Asistente de Voz</button>
      </div>

      <div style={{ ...styles.status, color: isStatusError ? 'red' : 'black' }}>
        <p>Estado: {statusMessage}</p>
      </div>

      <div style={styles.audioUpload}>
        <h2>Subir Archivo de Audio</h2>
        <input type="file" id="audioFile" accept="audio/*" onChange={handleAudioFileChange} style={{margin: '10px'}} />
        <button style={styles.button} onClick={processAudioFile} disabled={!audioFile}>Procesar Audio</button>
      </div>

      {jsonOutputData && (
        <div style={styles.jsonOutput}>
          {jsonOutputData.error ? (
            <p style={{color: 'red'}}>Error: {jsonOutputData.error}</p>
          ) : (
            <>
              {jsonOutputData.transcript && (
                <div style={styles.transcriptContainer}>
                  <h3>Transcripción:</h3>
                  <div style={styles.transcript}>{jsonOutputData.transcript}</div>
                </div>
              )}
              <div>
                <h3>Datos Extraídos:</h3>
                <pre>{JSON.stringify(jsonOutputData, null, 2)}</pre>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RealTimeConversation;